import { useMatch, useNavigate, useResolvedPath, useSearchParams } from 'react-router-dom';
import { useSessionActions, useSessionUser } from 'shared/store/session';
import { Avatar, Box, Button, Typography, Menu, MenuItem, Divider, AppBar, Toolbar, ListItemButton, ListItemText, List, useTheme, Icon, IconButton } from '@mui/material';
import { Logout, Settings } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { stringAvatar } from 'shared/utils';
import { useOrganizationActions, useSelectedOrganization, useOrgnizationList } from 'shared/store/organization';
import { AMBIENTE_NOT_RELEASE } from 'config/env';
import { useDrawerActions, useIsDrawerMenuOpen } from 'shared/store/drawer';
import { UsuariosService } from 'shared/services/usuarios/usuariosService';
import { reset } from 'chalk';

interface IListItemLinkProps {
  to: string;
  label: string;
  onClick: (() => void) | undefined;
}
const ListItemLink: React.FC<IListItemLinkProps> = ({ to, label, onClick }) => {
  const navigate = useNavigate();

  const resolvedPath = useResolvedPath(to);
  const match = useMatch({ path: resolvedPath.pathname, end: false });


  const handleClick = () => {
    navigate(to);
    onClick?.();
  };

  return (
    <ListItemButton selected={!!match} onClick={handleClick}>
      <ListItemText primary={label} />
    </ListItemButton>
  );
};



export const NavBar: React.FC = () => {
  const userActions = useSessionActions();
  const user = useSessionUser();
  const navigate = useNavigate();
  const nomeUsuario: string = user?.name || '';

  const organizacao = useSelectedOrganization();
  const organizacaoActions = useOrganizationActions();
  const organizacaoList = useOrgnizationList();

  const drawerAction = useDrawerActions();
  const isDrawerMenuOpen = useIsDrawerMenuOpen();

  const { t } = useTranslation();

  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    if (!organizacao && user) {

      UsuariosService.getById(Number(user.id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (result.FlagSolicNovaSenha === 'S') {
              navigate('/changepassword');
            }
            else {
              navigate('/organizationseletion');
            }
          }

        });

    }
  }, [organizacao]);


  const handleLogout = async () => {
    try {
      await userActions.logout();
    } catch (e) {
      // ignora erro no logout, vai ser deslogado de qualquer forma
    } finally {
      navigate('/login');
    }
  };

  const handleChangeOrg = () => {
    organizacaoActions.setSelectedOrganization(null);
  };

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar color='secondary' position="fixed" sx={{ display: 'flex', padding: '5px 20px', zIndex: (theme) => theme.zIndex.drawer + 2 }}>
      <Toolbar disableGutters>
        <IconButton sx={{ color: 'white' }} onClick={drawerAction.changeIsDrawerMenuOpen}>
          <Icon>menu</Icon>
        </IconButton>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ my: 2, ml: 3, mr: 3 }}
        >
          {t('global.namePortal')}
        </Typography>

        <Box sx={{ display: 'flex', flexGrow: 1, }}>
          {/* 
          <List component="nav" sx={{ display: 'flex' }}>

            {rotas.map((rota) => (
              <ListItemLink
                to={rota.to}
                key={rota.label}
                label={rota.label}
                onClick={undefined}
              />
            ))}
          </List>
        */}
        </Box>

        {AMBIENTE_NOT_RELEASE && (
          <>
            <Divider orientation="vertical" flexItem />

            <Box sx={{ textAlign: 'center', lineHeight: 1, ml: 1, mr: 1 }}>
              <Typography component="div" color='orange' sx={{ fontSize: '0.75rem' }}>
                Ambiente
              </Typography>
              <Typography component="div" color='orange'>
                {AMBIENTE_NOT_RELEASE}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />
          </>
        )}

        <Box sx={{ textAlign: 'center', lineHeight: 1, ml: 1, mr: 1 }}>
          <Typography component="div" color='white' sx={{ fontSize: '0.75rem' }}>
            {t('pages.navbar.organization')}
          </Typography>
          <Typography component="div" color='white'>
            {organizacao?.OrganizacaoNome}
          </Typography>
          <Button
            sx={{
              textTransform: 'none',
              padding: 0,
              minWidth: 0,
            }}
          >
            {organizacaoList.length > 1 && <Typography color='white' sx={{ fontSize: '0.75rem' }} onClick={handleChangeOrg}>{t('pages.navbar.change')}</Typography>}
          </Button>
        </Box>

        <Divider orientation="vertical" flexItem />
        <Box>
          <Button onClick={handleOpenUserMenu}>
            <Avatar variant="circular" {...stringAvatar(nomeUsuario)} sx={{ mr: 2 }} />
            <Typography color='white' sx={{ textTransform: 'none', fontSize: '0.9rem' }}>
              {t('messages.navbar.greeting')}
              <br />
              <Typography color='white' sx={{ fontSize: '1rem', fontStyle: 'italic' }}
                component="span"
              >
                {nomeUsuario}
              </Typography>
            </Typography>
          </Button>
        </Box>
        <Box sx={{ flexGrow: 0 }}>
          <Menu
            sx={{ mt: '45px' }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            <MenuItem  onClick={() => navigate('/changenewpassword')}>
              <Settings />
              <Typography textAlign="center">{t('components.menuItems.cadastro')}</Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout}>
              <Logout />
              <Typography textAlign="center">{t('components.menuItems.sair')}</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>


  );
};

function setAlert(arg0: { text: string; type: string; }) {
  throw new Error('Function not implemented.');
}
